<template>
  <div class="timeslot-stepper-container">

    <!-- Stepper Header -->
    <div style="margin-top: 20px;">
      <BaseStepper v-model="currentStep" :steps="steps" :allowForward="stepGuard" />
    </div>

    <!-- Conversation Header -->
    <div class="conversation-header">
      <div class="avatar-assistant">
        <i class="fa-solid fa-user-doctor"></i>
      </div>
      <div class="assistant-bubble">
        <transition name="message-fade" mode="out-in">
          <div :key="currentStep" class="message-content">
            {{ conversationMessage }}
          </div>
        </transition>
      </div>
    </div>

    <!-- Main Content Area (Input Deck) -->
    <div class="step-content-card conversation-deck">
      <transition name="fade-slide" mode="out-in">

        <!-- Date step removed; first step is now Days & Types -->

        <!-- Step 1: Days & Types -->
        <div v-if="currentStep === 0" key="step2" class="step-pane">
          <div class="section-block">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <label class="section-label mb-0">1. Select Working Day</label>
              <button class="btn-theme-secondary btn-sm" @click="openBulkUpdateModal">
                <i class="fa-solid fa-calendar-days"></i> Update Working Days
              </button>
            </div>
            <div class="days-grid">
              <div v-for="day in uniqueDays" :key="day" class="day-card"
                :class="{ selected: selectedDay === day, 'has-slots': hasAnySlotsForDay(day) }"
                @click="selectSingleDay(day)">
                <span class="day-name">{{ day }}</span>
                <i v-if="selectedDay === day" class="fa-solid fa-check-circle day-check-icon"></i>
                <div v-if="hasAnySlotsForDay(day)" class="day-dot"></div>
              </div>
            </div>
          </div>

          <transition name="fade">
            <!-- Delete warning with premium feel -->
            <div v-if="selectedDay && selectedAppointmentType && hasSlotsForType(selectedAppointmentType)"
              class="mt-4 delete-block">
              <div class="premium-alert warning">
                <div class="alert-icon"><i class="fa-solid fa-trash-can"></i></div>
                <div class="alert-content">
                  <strong>Clear Slots?</strong>
                  <span>Remove all slots for {{ selectedDay }} - {{ formatAppointmentType(selectedAppointmentType)
                  }}</span>
                </div>
                <button class="btn-theme-outline-danger" @click="deleteSelectedDayTypeSlot">Delete</button>
              </div>
            </div>
          </transition>

          <div class="section-block mt-5">
            <label class="section-label">2. Select Appointment Type</label>
            <div class="types-grid">
              <!-- Loading State -->
              <div v-if="isLoading" class="text-center py-4 text-muted w-100">
                <i class="fa-solid fa-circle-notch fa-spin me-2"></i> Loading appointment types...
              </div>

              <div v-else v-for="typeObj in appointmentTypes" :key="typeObj.id" class="type-card"
                :class="{ active: selectedAppointmentType === typeObj.id }" @click="selectType(typeObj.id)">
                <div class="type-icon-wrapper">
                  <i class="fa-solid fa-file-medical"></i>
                </div>
                <div class="type-info">
                  <span class="type-name">{{ typeObj.description || typeObj.label }}</span>
                  <span class="type-sub" v-if="hasSlotsForType(typeObj.id)">Configured</span>
                </div>
                <div class="custom-radio" :class="{ checked: selectedAppointmentType === typeObj.id }">
                </div>
              </div>
            </div>
          </div>

          <div v-if="stepErrors.types" class="error-box mt-4">
            <i class="fa-solid fa-circle-exclamation"></i> {{ stepErrors.types }}
          </div>
        </div>

        <!-- Step 3: Configure Slots -->
        <div v-else-if="currentStep === 1" key="step3" class="step-pane">
          <div class="config-header">
            <div>
              <p class="mb-3 text-muted">
                Configuring: <span class="highlight-text">{{ selectedDay || 'None' }}</span>
                <span v-if="selectedAppointmentType"> • <span class="highlight-text-light">{{
                  formatAppointmentType(selectedAppointmentType) }}</span></span>
              </p>
            </div>
          </div>

          <div v-if="!selectedAppointmentType" class="premium-alert info mb-4">
            <div class="alert-icon"><i class="fa-solid fa-hand-pointer"></i></div>
            <div class="alert-content">Tap an appointment type in the previous step to select it.</div>
          </div>

          <div v-if="selectedDay" class="config-form-wrapper">
            <div class="glass-panel">
              <div class="panel-header d-flex justify-content-between align-items-center mb-4">
                <h4 class="panel-title mb-0"><i class="fa-regular fa-clock"></i> Manage Slots for {{ selectedDay }}</h4>
                <button class="btn-theme-primary" @click="openCreateModal">
                  <i class="fa-solid fa-plus"></i> Add Timeslot
                </button>
              </div>

              <div v-if="isFetchingSlots" class="text-center py-5">
                <div class="spinner-border text-primary" role="status"></div>
                <p class="mt-2 text-muted">Loading slots...</p>
              </div>

              <div v-else class="available-slots-section mt-0">
                <transition-group name="list" tag="div" class="slots-container"
                  v-if="fetchedSlots && fetchedSlots.length">
                  <div v-for="(slot, idx) in fetchedSlots" :key="slot.id || idx" class="slot-pill"
                    style="justify-content: space-between; padding-right: 10px;"
                    :style="{ color: slot.active ? 'inherit' : 'red' }">
                    <span class="slot-time">
                      {{ formatTimeDisplay(slot.startTime) }} - {{ formatTimeDisplay(slot.endTime) }}
                    </span>
                    <div class="slot-actions ms-2 d-flex align-items-center">
                      <div class="slot-edit me-3 text-primary cursor-pointer" @click="openEditModal(slot)" title="Edit">
                        <i class="fa-solid fa-edit"></i>
                      </div>
                      <div v-if="slot.active" class="slot-delete text-danger cursor-pointer"
                        @click="handleDirectDelete(slot.id)" title="Delete">
                        <i class="fa-solid fa-trash-can"></i>
                      </div>
                      <div v-else class="slot-toggle text-success cursor-pointer" @click="toggleActive(slot)"
                        :title="slot.active ? 'Disable slot' : 'Enable slot'"
                        :aria-label="slot.active ? 'Disable slot' : 'Enable slot'">
                        <i
                          :class="slot.active ? 'fa-solid fa-toggle-on text-success' : 'fa-solid fa-toggle-off text-muted'"></i>
                      </div>
                    </div>
                  </div>
                </transition-group>
                <div v-else class="empty-state">
                  <div class="empty-icon"><i class="fa-regular fa-calendar-xmark"></i></div>
                  <p>No slots found for this day.</p>
                </div>
              </div>
            </div>
          </div>
          <CreateTimeslotModal :show="showCreateModal" :timeslot="editingSlot" :appointmentTypes="appointmentTypes"
            :selectedDay="selectedDay" :selectedAppointmentTypeId="selectedAppointmentType"
            :doctorId="doctor.id || doctor.doctorId" @close="showCreateModal = false" @saved="handleSlotSaved"
            @deleted="handleSlotDeleted" @timeslots-updated="handleTimeslotsUpdated" :assignNewTimeSlots="true" />
          <div v-if="stepErrors.config" class="error-box mt-4">
            <i class="fa-solid fa-circle-exclamation"></i> {{ stepErrors.config }}
          </div>
        </div>

        <!-- Step 3: Review & Copy -->
        <!-- Step 3: Review & Copy -->
        <div v-else-if="currentStep === 2" key="step4" class="step-pane">
          <div class="review-section mt-5">
            <h4 class="panel-title">Your Schedule Summary</h4>

            <div v-if="isFetchingSlots" class="text-center py-5">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="mt-2 text-muted">Retrieving slots from server...</p>
            </div>

            <!-- Show fetchedSlots directly in the summary -->
            <div v-else-if="fetchedSlots && fetchedSlots.length" class="review-grid-modern">
              <div class="review-card">
                <div class="review-card-header">
                  <span class="day">{{ selectedDay || 'Selected Day' }}</span>
                  <span class="type-badge">{{ formatAppointmentType(selectedAppointmentType) }}</span>
                </div>
                <div class="review-card-body button-review" @click="openDetailModal({
                  day: selectedDay,
                  appointmentType: selectedAppointmentType,
                  slots: fetchedSlots
                })">
                  <div class="slot-count success">
                    <i class="fa-regular fa-clock"></i> {{ fetchedSlots.length }} Slots
                  </div>
                  <div class="slot-range" v-if="fetchedSlots.length">
                    {{ formatSimpleTime(fetchedSlots[0].startTime) }} -
                    {{ formatSimpleTime(fetchedSlots[fetchedSlots.length - 1].endTime) }}
                  </div>
                  <div class="slot-details mt-3">
                    <div v-for="(slot, idx) in fetchedSlots.slice(0, 5)" :key="idx" class="slot-time-item">
                      <i class="fa-regular fa-clock me-2"></i>
                      {{ convertTo12Hour(slot.startTime) }} - {{ convertTo12Hour(slot.endTime) }}
                      <span v-if="!slot.active" class="badge bg-secondary ms-2">Inactive</span>
                    </div>
                    <div v-if="fetchedSlots.length > 5" class="text-muted small mt-2">
                      and {{ fetchedSlots.length - 5 }} more...
                    </div>
                  </div>
                  <div class="click-hint mt-2 text-primary small" @click="openDetailModal({
                    day: selectedDay,
                    appointmentType: selectedAppointmentType,
                    slots: fetchedSlots
                  })">
                    <i class="fa-solid fa-eye"></i> View All Details
                  </div>
                </div>
              </div>
            </div>

            <!-- Fallback to doctor.days if fetchedSlots is empty -->
            <div v-else-if="validConfiguredDays && validConfiguredDays.length" class="review-grid-modern">
              <div v-for="(d) in validConfiguredDays" :key="d.day + d.appointmentType" class="review-card clickable"
                @click="openDetailModal(d)">
                <div class="review-card-header">
                  <span class="day">{{ d.day }}</span>
                  <span class="type-badge">{{ formatAppointmentType(d.appointmentType) }}</span>
                </div>
                <div class="review-card-body">
                  <div class="slot-count success">
                    <i class="fa-regular fa-clock"></i> {{ d.slots.length }} Slots
                  </div>
                  <div class="slot-range" v-if="d.slots.length">
                    {{ formatSimpleTime(d.slots[0].startTime) }} -
                    {{ formatSimpleTime(d.slots[d.slots.length - 1].endTime) }}
                  </div>
                  <div class="click-hint mt-2 text-primary small">
                    <i class="fa-solid fa-eye"></i> View Details
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="empty-state">
              <div class="empty-icon"><i class="fa-regular fa-calendar-xmark"></i></div>
              <p>No slots configured yet. Please configure slots in the previous step.</p>
            </div>
          </div>
        </div>

      </transition>
    </div>

    <!-- Stepper Footer -->
    <div class="stepper-actions">
      <button class="btn-theme-nav prev" @click="prevStep" :disabled="currentStep === 0">
        <i class="fa-solid fa-arrow-left"></i> Back
      </button>
      <div class="action-spacer"></div>
      <button v-if="currentStep < steps.length - 1" class="btn-theme-nav next" @click="nextStep">
        Next Step <i class="fa-solid fa-arrow-right"></i>
      </button>
      <button v-else class="btn-theme-nav finish" @click="$router.push({ name: 'HospitalSettings' })">
        Back To Doctors <i class="fa-solid fa-check"></i>
      </button>
    </div>

    <!-- Slot Details Modal -->
    <!-- Slot Details Modal -->
    <transition name="fade">
      <div v-if="showDetailModal" class="slot-detail-modal-overlay" @click.self="closeDetailModal">
        <div class="slot-detail-modal">
          <div class="modal-header-theme">
            <h4 class="m-0 text-white">{{ selectedDetailDay.day }} - {{
              formatAppointmentType(selectedDetailDay.appointmentType) }}</h4>
            <button class="close-btn" @click="closeDetailModal">
              <i class="fa-solid fa-times"></i>
            </button>
          </div>
          <div class="modal-body-theme">
            <div v-if="selectedDetailDay.slots && selectedDetailDay.slots.length" class="modal-slots-grid">
              <div v-for="(slot, idx) in selectedDetailDay.slots" :key="idx" class="modal-slot-item"
                :class="{ 'inactive-slot': !slot.active }">
                <div class="slot-time-display">
                  {{ formatSimpleTime(slot.startTime) }} - {{ formatSimpleTime(slot.endTime) }}
                </div>
                <div v-if="!slot.active" class="inactive-badge">
                  <i class="fa-solid fa-ban"></i> Inactive
                </div>
              </div>
            </div>
            <div v-else class="text-muted text-center py-4">No slots available.</div>
          </div>
          <div class="modal-footer-theme">
            <div class="d-flex justify-content-between align-items-center w-100">
              <div class="text-muted small">
                <span class="me-3"><span class="status-dot active-dot"></span> Active</span>
                <span><span class="status-dot inactive-dot"></span> Inactive</span>
              </div>
              <button class="btn-theme-secondary" @click="closeDetailModal">Close</button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Bulk Update Days Modal -->
    <transition name="fade">
      <div v-if="showBulkUpdateModal" class="slot-detail-modal-overlay" @click.self="closeBulkUpdateModal">
        <div class="slot-detail-modal bulk-update-modal">
          <div class="modal-header-theme">
            <h4 class="m-0 text-white">
              <i class="fa-solid fa-calendar-days"></i> Update Working Days
            </h4>
            <button class="close-btn" @click="closeBulkUpdateModal">
              <i class="fa-solid fa-times"></i>
            </button>
          </div>
          <div class="modal-body-theme">
            <div class="mb-4">
              <label class="form-label-theme mb-3">Select Working Days</label>
              <p class="text-muted small mb-3">Choose the days when the doctor will be available for appointments.</p>
              <div class="mini-days-grid">
                <span v-for="day in uniqueDays" :key="day" class="mini-day"
                  :class="{ active: bulkSelectedDays.includes(day) }" @click="toggleBulkDay(day)">
                  {{ day }}
                </span>
              </div>
            </div>

            <div v-if="bulkSelectedDays.length" class="premium-alert info">
              <div class="alert-icon"><i class="fa-solid fa-info-circle"></i></div>
              <div class="alert-content">
                <strong>{{ bulkSelectedDays.length }} day(s)</strong> selected. Click "Update" to save the working days.
              </div>
            </div>

            <div v-if="isBulkUpdating" class="text-center py-3">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="mt-2 text-muted">Updating working days...</p>
            </div>
          </div>
          <div class="modal-footer-theme">
            <button class="btn-theme-secondary" @click="closeBulkUpdateModal" :disabled="isBulkUpdating">Cancel</button>
            <button class="btn-theme-primary" @click="applyBulkUpdate"
              :disabled="!bulkSelectedDays.length || isBulkUpdating">
              <i class="fa-solid fa-check"></i> Update
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import BaseStepper from "../common/BaseStepper.vue";
import CreateTimeslotModal from "../timeslots/CreateTimeslotModal.vue";
import { DoctorTimeSlotService } from '@/services/doctor-time-slots.service';
import { AppointmentTypeService } from '@/services/appointmentType.service';
export default {
  name: "DoctorTimeslotsForm",
  components: {
    BaseStepper,
    CreateTimeslotModal,
  },
  props: {
    doctor: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    selectedDay: {
      type: String,
      default: "",
    },
    selectedDays: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    "update:selected-day",
    "update:selected-days",
    "generate-slots",
    "remove-slot",
    "update:doctor",
    "delete-day-slots",
    "show-toast",
    "finished",
    "step-change"
  ],
  data() {
    return {
      currentStep: 0,
      steps: [
        { key: 'days', title: 'Days & Services' },
        { key: 'configure', title: 'Manage Slots' },
        { key: 'advanced', title: 'Review & Copy' },
      ],
      stepErrors: {},
      copySourceDay: "",
      copyTargetDays: [],
      selectedAppointmentType: "", // active editor type
      copySourceType: "",
      copyTargetTypes: [],
      showAdvanced: false,

      // Modal State
      showDetailModal: false,
      selectedDetailDay: {},

      // Create/Edit Modal State
      showCreateModal: false,
      editingSlot: null,

      // Slots Data
      fetchedSlots: [],
      isFetchingSlots: false,

      // Appointment Types
      appointmentTypes: [],
      isLoading: false,

      // Bulk Update Modal
      showBulkUpdateModal: false,
      bulkSelectedDays: [],
      isBulkUpdating: false,

    };
  },
  created() {
    this.fetchAppointmentTypes();
    this.$emit('step-change', this.currentStep);
  },
  computed: {
    conversationMessage() {
      switch (this.currentStep) {
        case 0:
          return "Hi Doctor! Which Days will you be working, and what Services will you offer?";
        case 1: {
          const day = this.selectedDay || 'your selected day';
          return `Okay, let's configure the details for ${day}. You can add, edit or delete slots here.`;
        }
        case 2:
          return "All set! Please review your schedule summary below.";
        default:
          return "Let's proceed.";
      }
    },
    canCreateSingle() {
      return this.selectedDay && this.selectedAppointmentType && this.currentDay && this.currentDay.startTime && this.currentDay.endTime;
    },
    canGenerate() {
      return this.selectedDay && this.selectedAppointmentType && this.currentDay && this.currentDay.startTime && this.currentDay.endTime && this.currentDay.numberOfPatients;
    },
    uniqueDays() {
      return [
        "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
      ];
    },

    copyFromDays() {
      const days = this.doctor?.days || [];
      const daysWithSlots = days
        .filter((d) => Array.isArray(d.slots) && d.slots.length > 0)
        .map((d) => d.day);
      return [...new Set(daysWithSlots)];
    },

    copyToDays() {
      return [
        "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
      ];
    },

    currentDay() {
      if (!this.selectedDay || !this.selectedAppointmentType) return null;

      let entry = this.doctor?.days?.find(
        (d) =>
          d.day === this.selectedDay &&
          d.appointmentType === this.selectedAppointmentType
      );

      if (!entry) {
        entry = {
          day: this.selectedDay,
          appointmentType: this.selectedAppointmentType,
          startTime: "",
          endTime: "",
          numberOfPatients: 0,
          break: 0,
          slots: [],
        };
      }

      return entry;
    },

    availableTypesForSelectedDay() {
      if (!this.selectedDay) return [];
      const dayTypes = this.doctor.days
        .filter((d) => d.day === this.selectedDay && d.appointmentType)
        .map((d) => d.appointmentType);
      return [...new Set(dayTypes)];
    },
    copySourceTypes() {
      if (!this.copySourceDay) return [];
      return [
        ...new Set(
          this.doctor.days
            .filter((d) => d.day === this.copySourceDay)
            .map((d) => d.appointmentType)
        ),
      ];
    },

    enabledDaysFromDoctor() {
      // Priority 1: Get enabled days from doctor.enabledDays
      if (this.doctor?.enabledDays) {
        if (Array.isArray(this.doctor.enabledDays)) {
          console.log('📅 Enabled days from API:', this.doctor.enabledDays);
          return this.doctor.enabledDays;
        }
      }

      // Priority 2: Check if doctor object has timeSlots as nested property
      if (this.doctor?.timeSlots?.days) {
        const daysFromSlots = [...new Set(this.doctor.timeSlots.days.map(d => d.day).filter(Boolean))];
        if (daysFromSlots.length > 0) {
          console.log('📅 Enabled days from timeSlots.days:', daysFromSlots);
          return daysFromSlots;
        }
      }

      // Priority 3: Fallback - extract unique days from doctor.days array
      if (this.doctor?.days && Array.isArray(this.doctor.days)) {
        const daysFromDoctorDays = [...new Set(this.doctor.days.map(d => d.day).filter(Boolean))];
        console.log('📅 Enabled days from doctor.days:', daysFromDoctorDays);
        return daysFromDoctorDays;
      }

      console.warn('⚠️ No enabled days found in doctor object');
      return [];
    },
  },
  watch: {
    // Watch for changes in doctor object to update enabled days
    'doctor.timeSlots.enabledDays': {
      handler(newEnabledDays) {
        if (newEnabledDays && Array.isArray(newEnabledDays)) {
          console.log('🔄 Doctor enabled days updated:', newEnabledDays);
        }
      },
      deep: true
    },

    currentStep(newStep) {
      if (newStep === 1) {
        this.fetchSlots();
      } else if (newStep === 2) {
        this.fetchAllConfiguredSlots();
      }
      this.$emit('step-change', newStep);
    },
    selectedAppointmentType() {
      if (this.currentStep === 1) this.fetchSlots();
    },

    selectedDay(newDay) {
      if (this.currentStep === 1) this.fetchSlots();

      if (!newDay) return;
      const exists = this.doctor.days.some(
        (d) =>
          d.day === newDay && d.appointmentType === this.selectedAppointmentType
      );

      if (!exists) {
        const firstType =
          this.doctor.days.find((d) => d.day === newDay)?.appointmentType || "";
        this.selectedAppointmentType = firstType;
      }
    },
  },

  methods: {
    convertToLocalTime(date, time) {
      if (!time) return null;

      let hours, minutes;

      // ISO format
      if (time.includes("T")) {
        const d = new Date(time);
        if (isNaN(d)) return null;
        hours = d.getUTCHours();
        minutes = d.getUTCMinutes();
      } else {
        const parts = time.split(":");
        if (parts.length < 2) return null;
        [hours, minutes] = parts.map(Number);
      }

      // subtract 6 hours
      hours = hours - 6;

      if (hours < 0) {
        hours += 24;
      }

      const localDate = new Date();
      localDate.setHours(hours);
      localDate.setMinutes(minutes);
      localDate.setSeconds(0);

      return localDate;
    },

    formatTimeDisplay(time) {
      if (!time) return "";

      const localDate = this.convertToLocalTime(null, time);
      if (!localDate) return "";

      return localDate.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },
    async fetchAppointmentTypes() {
      this.isLoading = true;
      try {
        const hospitalId = this.doctor?.hospitalId || this.$store.state.user.currentUser.objectId;
        const response = await AppointmentTypeService.getAll({ hospitalId });
        const data = response.data;
        if (data.status === 200) {
          this.appointmentTypes = data.data.data || [];
        } else {
          this.$emit("show-toast", {
            type: "error",
            message: data.message || "Failed to fetch appointment types"
          });
        }
      } catch (error) {
        console.error("Error fetching appointment types:", error);
        this.$emit("show-toast", {
          type: "error",
          message: "An error occurred while fetching appointment types"
        });
      } finally {
        this.isLoading = false;
      }
    },

    // Stepper navigation
    goToStep(idx) {
      if (idx === this.currentStep) return;
      // allow jumping back freely
      if (idx < this.currentStep) {
        this.currentStep = idx;
        return;
      }
      // validate intermediate step before moving forward
      const validators = [this.validateStep2, this.validateStep3];
      for (let i = this.currentStep; i < idx; i++) {
        const fn = validators[i];
        if (fn) {
          const ok = fn();
          if (!ok) return;
        }
      }
      this.currentStep = idx;
    },

    nextStep() {
      const okMap = [this.validateStep2, this.validateStep3];
      const validator = okMap[this.currentStep];
      if (validator && !validator()) return;
      if (this.currentStep < this.steps.length - 1) this.currentStep++;
    },

    prevStep() {
      if (this.currentStep > 0) this.currentStep--;
    },

    // Date validation removed (date step deleted)

    validateStep2() {
      this.stepErrors.types = '';
      if (!this.selectedAppointmentType) {
        this.stepErrors.types = 'Select an appointment type.';
        return false;
      }
      if (!this.selectedDay) {
        this.stepErrors.types = 'Select at least one working day.';
        return false;
      }
      return true;
    },

    validateStep3() {
      // Step 3 (index 1) verification
      this.stepErrors.config = '';
      if (!this.selectedDay || !this.selectedAppointmentType) {
        this.stepErrors.config = 'Select a day and appointment type to configure.';
        return false;
      }
      return true;
    },

    convertTo12Hour(time) {
      return this.formatTimeDisplay(time);
    },

    formatSimpleTime(time) {
      return this.formatTimeDisplay(time);
    },

    selectType(type) {
      this.selectedAppointmentType = type;
    },

    hasAnySlotsForDay(day) {
      const target = (day || "").toString().toLowerCase().trim();
      return (this.doctor.days || []).some((d) => {
        const dday = (d.day || "").toString().toLowerCase().trim();
        return dday === target && Array.isArray(d.slots) && d.slots.length > 0;
      });
    },

    deleteSlotsForDay(dayName) {
      if (!this.hasAnySlotsForDay(dayName)) return;
      if (!confirm(`Delete ALL slots for ${dayName}? This cannot be undone.`)) return;

      let updatedDays = (this.doctor.days || []).filter((d) => d.day !== dayName);

      this.$emit('update:doctor', { ...this.doctor, days: updatedDays });
      this.$emit('show-toast', { type: 'success', message: `All slots for ${dayName} have been deleted.` });

      if (this.selectedDay === dayName) {
        this.$emit('update:selected-day', '');
        this.selectedAppointmentType = '';
      }
    },

    formatAppointmentType(typeId) {
      const found = this.appointmentTypes.find(t => t.id === typeId);
      return found ? found.description || found.label : typeId;
    },

    hasSlotsForType(type) {
      const rec = (this.doctor.days || []).find(
        (d) => d.day === this.selectedDay && d.appointmentType === type
      );
      return rec && Array.isArray(rec.slots) && rec.slots.length > 0;
    },

    getDateForDay(dayName) {
      if (!dayName) return null;

      const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      const targetIndex = days.indexOf(dayName);
      if (targetIndex === -1) return null;

      const today = new Date();
      const currentDayIndex = today.getDay();

      let diff = targetIndex - currentDayIndex;
      if (diff <= 0) {
        diff += 7; // Get next occurrence
      }

      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + diff);

      const yyyy = targetDate.getFullYear();
      const mm = String(targetDate.getMonth() + 1).padStart(2, '0');
      const dd = String(targetDate.getDate()).padStart(2, '0');

      return `${yyyy}-${mm}-${dd}`;
    },

    async fetchSlots() {
      if (!this.selectedDay || !this.selectedAppointmentType) {
        this.fetchedSlots = [];
        return;
      }

      const date = this.getDateForDay(this.selectedDay);
      if (!date) return;

      this.isFetchingSlots = true;
      try {
        // Parameters for the API
        // API: /sync/v2/doctor-time-slots?date=...&appointmentType=...&doctorId=...&any=true
        const params = {
          day: this.selectedDay,
          appointmentType: this.selectedAppointmentType,
          doctorId: this.doctor.id || this.doctor.doctorId, // Ensure doctor ID is available
          hospitalId: this.doctor.hospitalId // If available, else API might infer or ignore
        };

        const res = await DoctorTimeSlotService.getSlotsV2(params);
        // Expecting ResponseWrapper with data
        if (res && res.data) {
          this.fetchedSlots = res.data.data;
          // this.syncDoctorDaysWithSlots();
        } else {
          this.fetchedSlots = [];
        }
      } catch (error) {
        console.error("Error fetching slots:", error);
        this.$emit("show-toast", {
          type: "error",
          message: "Failed to fetch timeslots."
        });
        this.fetchedSlots = [];
      } finally {
        this.isFetchingSlots = false;
      }
    },

    syncDoctorDaysWithSlots() {
      if (!this.selectedDay || !this.selectedAppointmentType || !this.fetchedSlots) return;

      const updatedDays = [...(this.doctor.days || [])];
      const index = updatedDays.findIndex(
        (d) => d.day === this.selectedDay && d.appointmentType === this.selectedAppointmentType
      );

      // Store RAW server times - NO CONVERSION
      const slotData = this.fetchedSlots.map(slot => ({
        ...slot,
        startTime: slot.startTime,  // Keep raw "18:00"
        endTime: slot.endTime,      // Keep raw "18:15"
      }));

      if (index !== -1) {
        updatedDays[index] = {
          ...updatedDays[index],
          slots: slotData
        };
      } else {
        updatedDays.push({
          day: this.selectedDay,
          appointmentType: this.selectedAppointmentType,
          slots: slotData,
          numberOfPatients: slotData.length,
          break: 0,
          startTime: slotData.length ? slotData[0].startTime : "",
          endTime: slotData.length ? slotData[slotData.length - 1].endTime : ""
        });
      }

      this.$emit("update:doctor", { ...this.doctor, days: updatedDays });
    },

    // syncDoctorDaysWithSlots() {
    //   if (!this.selectedDay || !this.selectedAppointmentType || !this.fetchedSlots) return;

    //   const updatedDays = [...(this.doctor.days || [])];
    //   const index = updatedDays.findIndex(
    //     (d) => d.day === this.selectedDay && d.appointmentType === this.selectedAppointmentType
    //   );

    //   const slotData = this.fetchedSlots.map(slot => ({
    //     ...slot,
    //     startTime: this.formatTimeDisplay(slot.startTime),
    //     endTime: this.formatTimeDisplay(slot.endTime),   // Store as LOCAL
    //   }));

    //   if (index !== -1) {
    //     updatedDays[index] = {
    //       ...updatedDays[index],
    //       slots: slotData
    //     };
    //   } else {
    //     updatedDays.push({
    //       day: this.selectedDay,
    //       appointmentType: this.selectedAppointmentType,
    //       slots: slotData,
    //       numberOfPatients: slotData.length,
    //       break: 0,
    //       startTime: slotData.length ? slotData[0].startTime : "",
    //       endTime: slotData.length ? slotData[slotData.length - 1].endTime : ""
    //     });
    //   }

    //   this.$emit("update:doctor", { ...this.doctor, days: updatedDays });
    // },

    async fetchAllConfiguredSlots() {
      this.isFetchingSlots = true;
      try {
        const params = {
          doctorId: this.doctor.id || this.doctor.doctorId,
          hospitalId: this.doctor.hospitalId
        };

        const res = await DoctorTimeSlotService.getTimeslotsV2(params);
        if (res && res.data) {
          // res.data is expected to be an array of objects, each having 'day', 'appointmentType', and 'slots'
          const allSlots = res.data;
          const updatedDays = [];

          // Grouping by day and appointmentType if necessary, or just mapping
          allSlots.forEach(item => {
            // item might have slots directly or it might be a nested structure
            // Based on getTimeslotsV2 in ManageAppointments.vue, it returns an array of docs with slots
            if (item.slots && item.slots.length > 0) {
              // Determine day name if not present
              const dayName = item.day;
              // Appointment types might be an array in some versions, but here it seems to be single in summary
              // If it's plural, we might need to handle it.

              updatedDays.push({
                day: dayName,
                appointmentType: item.appointmentType || (item.appointmentTypeIds && item.appointmentTypeIds[0]),
                slots: item.slots.map(s => ({
                  ...s,
                  startTime: this.formatTimeDisplay(s.startTime),
                  endTime: this.formatTimeDisplay(s.endTime)      // Store as LOCAL
                })),
                // Fallbacks
                startTime: this.formatTimeDisplay(item.slots[0].startTime),
                endTime: this.formatTimeDisplay(item.slots[item.slots.length - 1].endTime)
              });
            }
          });

          if (updatedDays.length > 0) {
            this.$emit("update:doctor", { ...this.doctor, days: updatedDays });
          }
        }
      } catch (error) {
        console.error("Error fetching all slots:", error);
      } finally {
        this.isFetchingSlots = false;
      }
    },

    openCreateModal() {
      this.editingSlot = null;
      this.showCreateModal = true;
    },

    openEditModal(slot) {
      // Slot from API might need mapping if CreateTimeslotModal expects specific fields
      // CreateTimeslotModal expects: { id, day, startTime, endTime, appointmentTypeIds, ... }
      // The API response slots usually have: _id/id, startTime, endTime, appointmentTypeIds

      // We need to inject 'day' because the modal requires it for validation/display
      // format startTime/endTime to HH:mm if they are full ISO or similar?
      // API typically returns HH:mm for startTime/endTime in this system based on history.

      this.editingSlot = {
        ...slot,
        startTime: this.formatTimeDisplay(slot.startTime),
        endTime: this.formatTimeDisplay(slot.endTime),
        day: this.selectedDay, // Force the current day context
        appointmentTypeIds: slot.appointmentTypeIds || [this.selectedAppointmentType]
      };
      this.showCreateModal = true;
    },

    handleSlotSaved() {
      this.fetchSlots();
      this.showCreateModal = false;
    },

    handleSlotDeleted() {
      this.fetchSlots();
      // Keep modal open or close? Usually close if deleted from modal.
      // If deleted from chip (outside modal), this is not called.
      // If deleted from modal, CreateTimeslotModal emits 'deleted'.
      this.showCreateModal = false;
    },

    handleTimeslotsUpdated(data) {
      // When user selects and adds timeslots from the available list
      console.log('Timeslots updated:', data);
      this.fetchSlots();
      this.showCreateModal = false;
    },

    handleDirectDelete(slotId) {
      if (!confirm("Are you sure you want to delete this timeslot?")) return;

      (async () => {
        try {
          await DoctorTimeSlotService.delete(slotId, this.selectedAppointmentType);
          this.$emit("show-toast", { type: "success", message: "Timeslot deleted." });
          this.fetchSlots();
        } catch (e) {
          this.$emit("show-toast", { type: "error", message: "Failed to delete timeslot." });
        }
      })();
    },

    async toggleActive(slot) {
      // Toggle active state for a timeslot using same payload shape as edit
      try {
        const id = slot.id || slot._id;
        if (!id) throw new Error('Invalid slot id');

        // Build payload similar to CreateTimeslotModal.update
        const payload = {
          startTime: slot.startTime,
          endTime: slot.endTime,
          appointmentTypeIds: slot.appointmentTypeIds || [this.selectedAppointmentType],
          slotId: id,
          active: !slot.active,
        };

        await DoctorTimeSlotService.update(id, payload);

        this.$emit('show-toast', { type: 'success', message: `Timeslot ${slot.active ? 'deactivated' : 'activated'}.` });
        this.fetchSlots();
      } catch (err) {
        console.error('toggleActive error', err);
        this.$emit('show-toast', { type: 'error', message: 'Failed to change timeslot state.' });
      }
    },

    toggleCopyTarget(day) {
      const index = this.copyTargetDays.indexOf(day);
      if (index === -1) {
        this.copyTargetDays.push(day);
      } else {
        this.copyTargetDays.splice(index, 1);
      }
    },

    toggleCopyTargetType(type) {
      const index = this.copyTargetTypes.indexOf(type);
      if (index === -1) this.copyTargetTypes.push(type);
      else this.copyTargetTypes.splice(index, 1);
    },

    copySlotsToSelectedDays() {
      if (!this.copySourceDay || !this.copySourceType) {
        alert("Please select source day and type.");
        return;
      }

      if (!this.copyTargetTypes.length || !this.copyTargetDays.length) {
        alert("Please select at least one target day and type.");
        return;
      }

      const sourceEntries = this.doctor.days.filter(
        (d) =>
          d.day === this.copySourceDay &&
          d.appointmentType === this.copySourceType &&
          Array.isArray(d.slots) &&
          d.slots.length > 0
      );

      if (!sourceEntries.length) {
        alert("Selected source has no slots to copy.");
        return;
      }

      let updatedDays = [...this.doctor.days];

      this.copyTargetDays.forEach((targetDay) => {
        sourceEntries.forEach((source) => {
          this.copyTargetTypes.forEach((targetType) => {
            // Prevent copy to self
            if (targetDay === this.copySourceDay && targetType === this.copySourceType) return;

            // Remove existing for target
            updatedDays = updatedDays.filter(
              (d) => !(d.day === targetDay && d.appointmentType === targetType)
            );

            // Add new
            updatedDays.push({
              ...source,
              day: targetDay,
              appointmentType: targetType,
              slots: source.slots.map((slot) => ({ ...slot })),
            });
          });
        });
      });

      this.$emit("update:doctor", { ...this.doctor, days: updatedDays });
      this.$emit("show-toast", {
        type: "success",
        message: `Schedule copied successfully.`,
      });
    },

    deleteSelectedDayTypeSlot() {
      const selectedType = this.selectedAppointmentType || "";
      const dayName = this.selectedDay;

      if (!confirm(`Clear all slots for ${dayName} - ${selectedType}?`)) return;

      const targetIndex = this.doctor.days.findIndex(
        (d) => d.day === dayName && d.appointmentType === selectedType
      );

      if (targetIndex !== -1) {
        let updatedDays = [...this.doctor.days];
        updatedDays.splice(targetIndex, 1);
        this.$emit("update:doctor", { ...this.doctor, days: updatedDays });
        this.$emit("show-toast", { type: "success", message: "Slots cleared." });
      }
    },

    // Changed to single-select only
    selectSingleDay(day) {
      // Set this day as the only selected day
      this.$emit('update:selected-day', day);
      this.$emit('update:selected-days', [day]);
    },

    toggleBulkDay(day) {
      const idx = this.bulkSelectedDays.indexOf(day);
      if (idx === -1) {
        this.bulkSelectedDays.push(day);
      } else {
        this.bulkSelectedDays.splice(idx, 1);
      }
    },

    openBulkUpdateModal() {
      // Pre-populate with already enabled days
      const enabledDays = this.enabledDaysFromDoctor;
      console.log('🚀 Opening bulk update modal', JSON.parse(JSON.stringify(this.doctor)));
      console.log('📦 Doctor timeSlots.enabledDays:', this.doctor?.enabledDays);
      console.log('� Computed enabledDays:', enabledDays);
      console.log('🎯 uniqueDays:', this.uniqueDays);

      // Ensure we're creating a fresh array for reactivity
      this.bulkSelectedDays = [];
      this.$nextTick(() => {
        this.bulkSelectedDays = Array.isArray(enabledDays) ? [...enabledDays] : [];
        console.log('✅ bulkSelectedDays set to:', this.bulkSelectedDays);

        // Verify which days should be active
        this.uniqueDays.forEach(day => {
          const isActive = this.bulkSelectedDays.includes(day);
          console.log(`  ${day}: ${isActive ? '✓ ACTIVE' : '✗ inactive'}`);
        });
      });

      this.showBulkUpdateModal = true;
    },

    closeBulkUpdateModal() {
      this.showBulkUpdateModal = false;
      this.bulkSelectedDays = [];
      this.isBulkUpdating = false;
    },

    async applyBulkUpdate() {
      if (!this.bulkSelectedDays.length) {
        this.$emit('show-toast', {
          type: 'warning',
          message: 'Please select at least one working day.'
        });
        return;
      }

      // Prepare payload for API
      const payload = {
        doctorId: this.doctor.id || this.doctor.doctorId,
        hospitalId: this.doctor.hospitalId,
        days: this.bulkSelectedDays
      };

      this.isBulkUpdating = true;

      try {
        const response = await DoctorTimeSlotService.updateSittingDates(payload);

        if (response && response.data) {
          this.$emit('show-toast', {
            type: 'success',
            message: `Successfully updated ${this.bulkSelectedDays.length} working day(s).`
          });

          // Refresh doctor data to persist the selected working days
          await this.refreshDoctorData();

          // Set the first selected day as active for quick editing
          if (this.bulkSelectedDays.length > 0) {
            this.$emit('update:selected-day', this.bulkSelectedDays[0]);
            this.$emit('update:selected-days', [this.bulkSelectedDays[0]]);
          }

          this.closeBulkUpdateModal();
        }
      } catch (error) {
        console.error('Error updating sitting dates:', error);
        this.$emit('show-toast', {
          type: 'error',
          message: error.message || 'Failed to update working days. Please try again.'
        });
      } finally {
        this.isBulkUpdating = false;
      }
    },

    async refreshDoctorData() {
      try {
        const doctorId = this.doctor.id || this.doctor.doctorId;
        const hospitalId = this.doctor.hospitalId;

        if (!doctorId || !hospitalId) {
          console.warn('Missing doctorId or hospitalId for refresh');
          return;
        }

        console.log('🔄 Refreshing doctor data...', { doctorId, hospitalId });

        const { DoctorsService } = await import('@/services/doctors.service');
        const response = await DoctorsService.getById(doctorId, { hospitalId });

        if (response && response.data) {
          console.log('✅ Received updated doctor data from API');
          console.log('📦 API enabledDays:', response.data?.timeSlots?.enabledDays);

          // Map the enabledDays to the root level for the parent component
          const updatedDoctor = {
            ...response.data,
            enabledDays: response.data?.timeSlots?.enabledDays || []
          };

          console.log('🔄 Emitting updated doctor with enabledDays:', updatedDoctor.enabledDays);

          // Emit event to parent to update doctor data
          this.$emit('update:doctor', updatedDoctor);

          // Force a re-check of enabled days after update
          this.$nextTick(() => {
            console.log('✨ After update - doctor.enabledDays:', this.doctor.enabledDays);
          });
        }
      } catch (error) {
        console.error('Error refreshing doctor data:', error);
        // Don't show error toast here as the main operation succeeded
      }
    },

    updateField(field, value) {
      this.$emit("update:doctor", { ...this.doctor, [field]: value });
    },

    async updateDayField(field, value) {
      const selectedType = this.selectedAppointmentType || "";
      let updatedDays = [...this.doctor.days];
      const existingIndex = updatedDays.findIndex(
        (d) => d.day === this.selectedDay && d.appointmentType === selectedType
      );

      const val = (field === "numberOfPatients" || field === "break") ? Number(value) : value;

      if (existingIndex !== -1) {
        updatedDays[existingIndex] = {
          ...updatedDays[existingIndex],
          [field]: val,
        };
      } else {
        updatedDays.push({
          day: this.selectedDay,
          appointmentType: selectedType,
          startTime: field === "startTime" ? val : "",
          endTime: field === "endTime" ? val : "",
          numberOfPatients: field === "numberOfPatients" ? val : 0,
          break: field === "break" ? val : 0,
          slots: [],
        });
      }
      this.$emit("update:doctor", { ...this.doctor, days: updatedDays });
    },

    // async updateSittingDates() {
    //   // This method is now integrated into finish()
    //   try {
    //     console.log("selectedDays", this.selectedDays);
    //     if (this.selectedDays && Array.isArray(this.selectedDays) && this.selectedDays.length) {
    //       const payload = {
    //         doctorId: this.doctor.id || this.doctor.doctorId || null,
    //         hospitalId: this.doctor.hospitalId || null,
    //         days: this.selectedDays
    //       };

    //       const resp = await DoctorTimeSlotService.updateSittingDates(payload);
    //       if (resp && resp.success) {
    //         this.$emit('show-toast', { type: 'success', message: resp.message || 'Enabled days updated.' });
    //       } else {
    //         this.$emit('show-toast', { type: 'error', message: resp?.message || 'Failed to update enabled days.' });
    //       }
    //     }
    //   } catch (err) {
    //     console.error('Error updating sitting dates:', err);
    //     this.$emit('show-toast', { type: 'error', message: 'Failed to update enabled days.' });
    //   }
    // },

    finish() {
      if (!this.validateStep2()) { this.currentStep = 0; return; }
      if (!this.validateStep3()) { this.currentStep = 1; return; }
      // Step 3 validation is per-configuration, so we check global state here

      const hasSlots = this.doctor.days.some(d => Array.isArray(d.slots) && d.slots.length > 0);

      if (!hasSlots) {
        this.$emit('show-toast', { type: 'warning', message: 'Please generate at least one slot before saving.' });
        return;
      }

      this.$emit('show-toast', { type: 'success', message: 'Timeslots configuration saved.' });
      this.$emit('finished');
    },

    openDetailModal(day) {
      // Make sure slots are properly formatted with active status
      if (day.slots) {
        // Ensure each slot has the active property (default to true if not set)
        this.selectedDetailDay = {
          ...day,
          slots: day.slots.map(slot => ({
            ...slot,
            active: slot.active !== undefined ? slot.active : true // Default to active if not specified
          }))
        };
      } else {
        this.selectedDetailDay = day;
      }
      this.showDetailModal = true;
    },
    closeDetailModal() {
      this.showDetailModal = false;
    },

    // convertTo12Hour(time24) {
    //   if (!time24) return "";
    //   let [hours, minutes] = time24.split(":").map(Number);
    //   let period = hours >= 12 ? "PM" : "AM";
    //   hours = hours % 12 || 12;
    //   return `${hours}:${minutes?.toString()?.padStart(2, "0")} ${period}`;
    // },

    timeToMinutes(time) {
      const [hours, minutes] = time.split(":").map(Number);
      return hours * 60 + minutes;
    },

    minutesToTime(minutes) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
    },
    stepGuard(from, to) {
      const validators = [
        this.validateStep2,
        this.validateStep3
      ];

      for (let i = from; i < to; i++) {
        if (validators[i] && !validators[i]()) {
          return false;
        }
      }
      return true;
    }
  },
};
</script>

<style scoped>
/* VARIABLES */
.timeslot-stepper-container {
  --primary-dark: #0144A6;
  --primary-bright: #219DFF;
  --accent-yellow: #ffba33;
  --text-grey: #606060;
  --bg-light: #f4f6fa;
  --bg-white: #ffffff;
  --border-radius-card: 20px;
  --border-radius-btn: 30px;
  --border-radius-input: 40px;
  --font-family: 'Poppins', sans-serif;

  font-family: var(--font-family);
  display: flex;
  flex-direction: column;
  gap: 25px;
  max-width: 1100px;
  margin: 0 auto;
}

/* CONVERSATION HEADER */
.conversation-header {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  padding: 0 15px;
  margin-bottom: 10px;
}

.avatar-assistant {
  width: 50px;
  height: 50px;
  background: var(--primary-bright);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  box-shadow: 0 4px 10px rgba(33, 157, 255, 0.3);
  flex-shrink: 0;
}

/* STEPPER HEADER */
.stepper-header-modern {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 15px;
}

.step-item {
  display: flex;
  align-items: center;
  flex: 1;
  cursor: pointer;
  opacity: 0.5;
  transition: all 0.3s ease;
}

.step-item.active,
.step-item.completed {
  opacity: 1;
}

.step-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: white;
  border: 2px solid #e0e0e0;
  color: var(--text-grey);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  z-index: 2;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.step-item.active .step-circle {
  background: var(--primary-bright);
  color: #fff;
  border-color: var(--primary-bright);
  box-shadow: 0 0 0 5px rgba(0, 123, 255, 0.2);
  transform: scale(1.1);
}

.step-item.completed .step-circle {
  background: var(--primary-dark);
  color: #fff;
  border-color: var(--primary-dark);
}

.step-label {
  margin-left: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--primary-dark);
  white-space: nowrap;
}

.step-line {
  flex: 1;
  height: 3px;
  background: #e0e0e0;
  margin: 0 15px;
  border-radius: 2px;
}

.step-item.completed .step-line {
  background: var(--primary-dark);
}

.assistant-bubble {
  background: white;
  padding: 15px 25px;
  border-radius: 0 20px 20px 20px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
  font-size: 1.1rem;
  color: var(--primary-dark);
  font-weight: 500;
  line-height: 1.5;
  position: relative;
  border: 1px solid #eef2f6;
  max-width: 800px;
}

.assistant-bubble::after {
  content: '';
  position: absolute;
  top: 0;
  left: -10px;
  width: 0;
  height: 0;
  border-top: 15px solid white;
  border-left: 10px solid transparent;
}

/* Message Animation */
.message-fade-enter-active,
.message-fade-leave-active {
  transition: all 0.3s ease;
}

.message-fade-enter-from {
  opacity: 0;
  transform: translateY(5px);
}

.message-fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* CONTENT CARD (DECK) */
.step-content-card {
  background: var(--bg-white);
  border-radius: var(--border-radius-card);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  padding: 40px;
  min-height: 450px;
  border: 1px solid rgba(28, 52, 118, 0.05);
  /* Make it feel like the response area */
  border-top: 4px solid var(--primary-bright);
}

.step-heading {
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary-dark);
  margin-bottom: 8px;
}

.step-subheading {
  color: var(--text-grey);
  font-size: 1rem;
  margin-bottom: 35px;
  font-weight: 400;
}

/* FORMS & INPUTS */
.form-card-internal {
  /* background: #fbfbfb; */
  /* padding: 10px; */
}

.form-label-theme {
  display: block;
  font-weight: 600;
  margin-bottom: 12px;
  color: var(--text-grey);
  font-size: 1.05rem;
}

.input-theme-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.form-control-theme {
  width: 100%;
  padding: 16px 20px 16px 50px;
  border: 3px solid var(--primary-dark);
  border-radius: var(--border-radius-input);
  font-size: 1rem;
  color: #060606;
  font-weight: 500;
  transition: all 0.2s;
  background: #fff;
  font-family: var(--font-family);
}

.form-control-theme:focus {
  border-color: var(--primary-bright);
  outline: none;
  box-shadow: 0 0 0 4px rgba(0, 123, 255, 0.1);
}

.icon-indicator {
  position: absolute;
  left: 20px;
  color: var(--primary-dark);
  font-size: 1.2rem;
  pointer-events: none;
}

/* STEP 2: DAYS & TYPES */
.section-label {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--primary-dark);
  margin-bottom: 15px;
  display: block;
}

.days-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 15px;
}

.day-card {
  border: 2px solid #e0e0e0;
  border-radius: 15px;
  /* padding: 10px 10px; */
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  background: #fff;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.02);
}

.day-card:hover {
  border-color: var(--primary-bright);
  transform: translateY(-3px);
  box-shadow: 0 8px 15px rgba(0, 123, 255, 0.15);
}

.day-card.selected {
  border-color: var(--primary-bright);
  background: #f0f5ff;
}

.day-name {
  font-weight: 600;
  color: var(--text-grey);
  font-size: 1rem;
}

.day-card.selected .day-name {
  color: var(--primary-dark);
  font-weight: 700;
}

.day-check-icon {
  position: absolute;
  top: 8px;
  right: 8px;
  color: var(--primary-bright);
  font-size: 1.1rem;
}

.day-dot {
  position: absolute;
  bottom: 10px;
  width: 6px;
  height: 6px;
  background: var(--accent-yellow);
  border-radius: 50%;
}

.types-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
}

.type-card {
  display: flex;
  align-items: center;
  padding: 20px;
  border: 2px solid #e0e0e0;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s;
  background: white;
  position: relative;
  overflow: hidden;
}

.type-card:hover {
  border-color: var(--primary-bright);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
}

.type-card.active {
  border-color: var(--primary-bright);
  background: linear-gradient(145deg, #ffffff, #f0f5ff);
}

.type-card.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  background: var(--primary-bright);
}

.type-icon-wrapper {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: #f4f6fa;
  color: var(--primary-dark);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  margin-right: 15px;
}

.type-card.active .type-icon-wrapper {
  background: var(--primary-bright);
  color: white;
}

.type-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.type-name {
  font-weight: 700;
  font-size: 1rem;
  color: var(--primary-dark);
}

.type-sub {
  font-size: 0.8rem;
  color: var(--primary-bright);
  font-weight: 600;
}

.custom-checkbox {
  width: 24px;
  height: 24px;
  border: 2px solid #ccc;
  border-radius: 6px;
  position: relative;
  transition: all 0.2s;
}

.custom-checkbox.checked {
  background: var(--primary-bright);
  border-color: var(--primary-bright);
}

.custom-checkbox.checked::after {
  content: '\f00c';
  font-family: 'Font Awesome 6 Free';
  font-weight: 900;
  color: white;
  font-size: 14px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* ALERTS */
.premium-alert {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  border-radius: 15px;
  margin-bottom: 20px;
  gap: 15px;
}

.premium-alert.warning {
  background: #fff5e6;
  border: 1px solid #ffeeba;
  color: #856404;
}

.premium-alert.info {
  background: aliceblue;
  border: 1px solid #daeaff;
  color: var(--primary-dark);
}

.alert-icon {
  font-size: 1.5rem;
  opacity: 0.8;
}

.alert-content {
  flex: 1;
  font-size: 0.95rem;
  display: flex;
  flex-direction: column;
}

.btn-theme-outline-danger {
  background: transparent;
  border: 2px solid #dc3545;
  color: #dc3545;
  border-radius: 30px;
  padding: 6px 20px;
  font-weight: 600;
  transition: all 0.2s;
}

.btn-theme-outline-danger:hover {
  background: #dc3545;
  color: white;
}

/* STEP 3 CONFIG */
.glass-panel {
  background: #ffffff;
  border: 1px solid #eee;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
}

.panel-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--primary-dark);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.badge-count {
  background: var(--primary-bright);
  color: white;
  font-size: 0.8rem;
  padding: 2px 8px;
  border-radius: 10px;
  margin-left: 10px;
}

.config-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 30px;
}

.btn-theme-primary {
  background: var(--primary-dark);
  color: #fff;
  border: none;
  font-family: var(--font-family);
  font-weight: 600;
  padding: 12px 30px;
  border-radius: var(--border-radius-btn);
  font-size: 1rem;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.btn-theme-primary:hover:not(:disabled) {
  background: #142555;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(28, 52, 118, 0.3);
}

.btn-theme-secondary {
  background: #eef2f6;
  color: var(--primary-dark);
  border: none;
  font-family: var(--font-family);
  font-weight: 600;
  padding: 12px 30px;
  border-radius: var(--border-radius-btn);
  font-size: 1rem;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.btn-theme-secondary:hover:not(:disabled) {
  background: #dbe4ef;
  color: var(--primary-dark);
}

.btn-theme-primary:disabled,
.btn-theme-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.actions-group {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
}

/* .slots-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
} */

.slots-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.slot-pill {
  justify-content: space-between;
  width: 100%;
}

.slot-pill {
  background: #ffffff;
  border: 2px solid #e0e0e0;
  border-radius: 30px;
  padding: 8px 18px;
  font-weight: 600;
  color: var(--text-grey);
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.2s;
}

.slot-pill:hover {
  border-color: var(--primary-bright);
  color: var(--primary-dark);
}

.slot-delete {
  color: #ff6b6b;
  cursor: pointer;
  padding: 2px;
}

.slot-delete:hover {
  color: #fa5252;
  transform: scale(1.1);
}

/* STEP 4: REVIEW & ADVANCED */
.advanced-box {
  background: #f8fbff;
  border: 2px dashed #dbe4ef;
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.3s;
}

.advanced-box:hover {
  border-color: var(--primary-bright);
}

.advanced-header {
  padding: 20px 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  color: var(--primary-dark);
  transition: background 0.3s;
}

.advanced-header:hover {
  background: #f1f6fc;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-bright);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  font-size: 1.1rem;
}

.header-text-group {
  display: flex;
  flex-direction: column;
}

.header-text-group .title {
  font-size: 1rem;
  font-weight: 700;
}

.header-text-group .subtitle {
  font-size: 0.85rem;
  color: var(--text-grey);
  font-weight: 400;
}

.toggle-indicator {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--primary-bright);
  font-size: 0.9rem;
}

.advanced-content {
  padding: 0 30px 30px 30px;
}

.guide-text {
  background: #fff;
  padding: 15px;
  border: 1px solid #e0e0e0;
  border-left: 4px solid var(--accent-yellow);
  border-radius: 8px;
  margin-bottom: 25px;
  font-size: 0.95rem;
  color: var(--text-grey);
  display: flex;
  gap: 12px;
  align-items: center;
}

.guide-text i {
  color: var(--accent-yellow);
  font-size: 1.2rem;
}

.form-select-theme {
  width: 100%;
  padding: 12px;
  border: 2px solid #dbe4ef;
  border-radius: 12px;
  color: var(--text-grey);
  font-family: var(--font-family);
  font-size: 1rem;
}

.mini-days-grid span,
.mini-types-grid span {
  display: inline-block;
  padding: 6px 16px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  margin-right: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.mini-days-grid span:hover,
.mini-types-grid span:hover {
  border-color: var(--primary-bright);
}

.mini-days-grid span.active,
.mini-types-grid span.active {
  background: var(--primary-bright);
  color: white;
  border-color: var(--primary-bright);
  box-shadow: 0 4px 10px rgba(0, 123, 255, 0.2);
}

.review-grid-modern {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
}

.review-card {
  background: white;
  border: 1px solid #eee;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  transition: all 0.3s;
}

.review-card.clickable {
  cursor: pointer;
}

.review-card.clickable:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 123, 255, 0.15);
  border-color: var(--primary-bright);
}

.review-card-header {
  background: var(--bg-light);
  padding: 12px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  color: var(--primary-dark);
}

.type-badge {
  background: white;
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 0.75rem;
  color: var(--primary-bright);
  font-weight: 700;
}

.review-card-body {
  padding: 15px;
}

.slot-count {
  font-weight: 700;
  color: var(--text-grey);
  margin-bottom: 5px;
}

.slot-count.success {
  color: #20c997;
}

/* STEPPER NAV BUTTONS */
.stepper-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
}

.btn-theme-nav {
  border: none;
  font-family: var(--font-family);
  font-weight: 600;
  padding: 14px 35px;
  border-radius: var(--border-radius-btn);
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-theme-nav.next,
.btn-theme-nav.finish {
  background: var(--primary-bright);
  color: white;
  box-shadow: 0 10px 20px rgba(0, 123, 255, 0.2);
}

.btn-theme-nav.next:hover,
.btn-theme-nav.finish:hover {
  background: #0069d9;
  transform: translateY(-2px);
  box-shadow: 0 15px 25px rgba(0, 123, 255, 0.35);
}

.btn-theme-nav.next:disabled {
  background: #b8b9ba;
  cursor: not-allowed;
}

.btn-theme-nav.finish {
  background: var(--primary-dark);
  transform: translateY(0px);
  box-shadow: none;
}

.btn-theme-nav.finish:hover {
  background: #142555;
  box-shadow: 0 15px 25px rgba(28, 52, 118, 0.3);
}

.btn-theme-nav.prev {
  background: #eef2f6;
  color: var(--text-grey);
}

.btn-theme-nav.prev:hover {
  background: #dbe4ef;
  color: var(--primary-dark);
}

/* MODAL */
.slot-detail-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.slot-detail-modal {
  background: white;
  width: 90%;
  max-width: 800px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease-out;
}

.modal-header-theme {
  background: var(--primary-dark);
  padding: 15px 25px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.close-btn {
  background: transparent;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.2s;
}

.close-btn:hover {
  opacity: 1;
}

.modal-body-theme {
  padding: 25px;
  max-height: 60vh;
  overflow-y: auto;
}

.modal-slots-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
}

.modal-slot-item {
  background: #f4f6fa;
  border: 1px solid #e0e0e0;
  padding: 8px;
  text-align: center;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--primary-dark);
}

.modal-footer-theme {
  padding: 15px 25px;
  border-top: 1px solid #eee;
  text-align: right;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* UTILITIES */
.highlight-text {
  color: var(--primary-dark);
  font-weight: 700;
}

.highlight-text-light {
  color: var(--primary-bright);
  font-weight: 600;
}

.error-box {
  background: #fff8f8;
  color: #e03131;
  padding: 15px;
  border-radius: 12px;
  border: 1px solid #ffc9c9;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;
}

.rotated {
  transform: rotate(180deg);
}

.transition-icon {
  transition: transform 0.3s ease;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #adb5bd;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 15px;
  opacity: 0.5;
}

/* RESPONSIVE */
@media (max-width: 768px) {
  .step-label {
    display: none;
  }

  .config-grid {
    grid-template-columns: 1fr 1fr;
  }

  .stepper-actions {
    padding: 0 10px;
  }

  .step-content-card {
    padding: 25px;
  }

  .conversation-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .assistant-bubble::after {
    top: -10px;
    left: 20px;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 15px solid white;
    border-top: none;
  }
}

.slot-time-item {
  padding: 4px 0;
  font-size: 0.9rem;
  color: var(--text-grey);
  border-bottom: 1px dashed #eee;
}

.slot-time-item:last-child {
  border-bottom: none;
}

.badge {
  font-size: 0.7rem;
  padding: 3px 6px;
  border-radius: 4px;
}

.badge.bg-secondary {
  background-color: #6c757d;
  color: white;
}

/* Modal slot styling with active/inactive states */
.modal-slot-item {
  background: #f4f6fa;
  border: 1px solid #e0e0e0;
  padding: 10px;
  text-align: center;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--primary-dark);
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.modal-slot-item.inactive-slot {
  background: #fff0f0;
  border-color: #ffcdcd;
  color: #dc3545;
  opacity: 0.9;
}

.modal-slot-item.inactive-slot .slot-time-display {
  color: #dc3545;
  font-weight: 600;
}

.inactive-badge {
  font-size: 0.7rem;
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 2px;
}

.inactive-badge i {
  font-size: 0.65rem;
}

/* Status dots for legend */
.status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 4px;
}

.active-dot {
  background-color: #28a745;
  box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.2);
}

.inactive-dot {
  background-color: #dc3545;
  box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.2);
}

/* Update existing modal footer to accommodate legend */
.modal-footer-theme {
  padding: 15px 25px;
  border-top: 1px solid #eee;
}

.modal-footer-theme .d-flex {
  display: flex;
  align-items: center;
}

.button-review {
  cursor: pointer;
  transition: all 0.2s ease;
}

.button-review:hover {
  transform: translateY(-2px);
}
</style>
